<template>
  <div class="wrapper" :class="{ mobile: !isLargeScreen }">
    <div class="overview">
      <div class="overview-title">
        <h2>Overview</h2>
      </div>
      <div class="overview-contents">
        <div>
          <FiringAlertsCard
            :firingAlertsAmountOfSelectedASN="firingAlertsAmountOfSelectedASN"
            :lastFiringAlertObj="lastFiringAlertObj"
          >
          </FiringAlertsCard>
        </div>
        <div>
          <TopTriggerCard
            :topTriggerList="topTriggerList"
            @onViewDetails="handleViewTriggerDetails"
          ></TopTriggerCard>
        </div>
      </div>
    </div>
    <div class="alerts">
      <div v-if="isLargeScreen">
        <div class="alerts-title">
          <h2>Your alerts</h2>
          <div class="search-and-create">
            <div class="search-alert">
              <SearchBox
                placeholder="Search for alert name"
                :isWithIcon="true"
              ></SearchBox>
            </div>
            <div class="create-alert">
              <button class="btn btn-primary" @click="showCreateAlertModal">
                New alert
              </button>
            </div>
            <div class="icon-container">
              <fa
                v-if="!isExpandStatusIndicators"
                icon="question-circle"
                v-tooltip.top="{ content: 'Alert status indicators' }"
                @click="isExpandStatusIndicators = true"
              ></fa>
              <fa
                v-if="isExpandStatusIndicators"
                icon="chevron-up"
                @click="isExpandStatusIndicators = false"
              ></fa>
            </div>
          </div>
        </div>
        <div
          ref="accordionContent"
          class="status-indicators-content"
          :class="{ show: isExpandStatusIndicators }"
        >
          <h5>Alert status indicators</h5>
          <AlertStatusIndicators></AlertStatusIndicators>
        </div>
      </div>
      <div v-else class="mobile">
        <div class="alerts-title">
          <h2>Your alerts</h2>
          <div class="search-and-create">
            <div class="create-alert">
              <button class="btn btn-primary" @click="showCreateAlertModal">
                New alert
              </button>
            </div>
          </div>
        </div>
        <div
          class="indicator-toggle"
          @click="isExpandStatusIndicators = !isExpandStatusIndicators"
        >
          <p>Alert status indicators</p>
          <div class="icon-wrapper">
            <fa
              icon="chevron-down"
              :class="{ 'fa-rotate-180': isExpandStatusIndicators }"
            ></fa>
          </div>
        </div>
        <div
          ref="accordionContent"
          class="status-indicators-content"
          :class="{ show: isExpandStatusIndicators }"
        >
          <AlertStatusIndicators></AlertStatusIndicators>
        </div>
        <div class="icon-group-and-search">
          <div class="icon-group">
            <fa
              icon="sort-amount-down"
              class="icon"
              :class="{ active: isShowSortTool }"
              @click="toggleIsShowSortTool"
            ></fa>
            <fa
              icon="filter"
              class="icon"
              :class="{ active: isShowFilterTool }"
              @click="toggleIsShowFilterTool"
            ></fa>
          </div>
          <div class="search-alert">
            <SearchBox
              placeholder="Search for alert name"
              :isWithIcon="true"
              @onCloseSearchBox="closeSearchBox"
            ></SearchBox>
          </div>
        </div>
        <div v-if="isShowSortTool" class="tool">
          <SortAndFilterTool
            :toolConfig="sortToolConfig"
            @onToolChange="handleSortToolChange"
          ></SortAndFilterTool>
        </div>
        <div v-if="isShowFilterTool" class="tool">
          <SortAndFilterTool
            :toolConfig="filterToolConfig"
            @onToolChange="handleFilterToolChange"
          ></SortAndFilterTool>
        </div>
      </div>
      <div v-if="isLargeScreen" class="alerts-content">
        <AlertTable
          @onEditAlert="editAlert"
          @onSnoozeAlert="snoozeAlert"
          @onUnSnoozeAlert="unSnooze"
          @onDeleteAlert="deleteAlert"
        ></AlertTable>
      </div>
      <div v-else class="alerts-content">
        <MobileAlertCard
          :sortType="sortType"
          :filterType="filterType"
          @onEditAlert="editAlert"
          @onSnoozeAlert="snoozeAlert"
          @onUnSnoozeAlert="unSnooze"
          @onDeleteAlert="deleteAlert"
          @onCloseFilterTool="handleCloseFilterTool"
        ></MobileAlertCard>
      </div>
    </div>
    <PrefixDetails
      v-if="isShowTriggerDetailsModal"
      @onCloseModal="setIsShowTriggerDetailsModal(false)"
      :prefix="selectedTrigger"
      :prefixHistoryData="selectedTriggerDetails"
    ></PrefixDetails>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import FiringAlertsCard from './overviewCards/FiringAlertsCard.vue';
import TopTriggerCard from './overviewCards/TopTriggerCard.vue';
import AlertTable from './AlertTable.vue';
import SearchBox from '../../ui/SearchBox.vue';
import SortAndFilterTool from './alertTableParts/SortAndFilterTool.vue';
import MobileAlertCard from './alertTableParts/MobileAlertCard.vue';
import PrefixDetails from './PrefixDetails.vue';
import {
  ALERT_SORT_OPTION,
  ALERT_FILTER_OPTION
} from '../../../constants/alertsConstant';
import AlertStatusIndicators from '../../../components/ui/AlertStatusIndicators.vue';
import { scrollToShowElementBottom } from '../../../utils/BOM';

export default {
  name: 'Overview',
  components: {
    FiringAlertsCard,
    TopTriggerCard,
    AlertTable,
    SearchBox,
    SortAndFilterTool,
    MobileAlertCard,
    PrefixDetails,
    AlertStatusIndicators
  },
  data() {
    return {
      isShowSearchAlert: false,
      isShowSortTool: false,
      isShowFilterTool: false,
      sortType: 0,
      filterType: 0,
      sortToolConfig: {
        placeholder: 'Sort',
        toolList: [
          { id: ALERT_SORT_OPTION.NONE, text: 'None' },
          {
            id: ALERT_SORT_OPTION.NAME_ASCENDING,
            text: 'Name (alphabetically)'
          },
          {
            id: ALERT_SORT_OPTION.LAST_TRIGGER_NEWEST_FIRST,
            text: 'Last trigger: newest first'
          },
          {
            id: ALERT_SORT_OPTION.LAST_TRIGGER_OLDEST_FIRST,
            text: 'Last trigger: oldest first'
          }
        ]
      },
      filterToolConfig: {
        placeholder: 'Filter',
        toolList: [
          { id: ALERT_FILTER_OPTION.NONE, text: 'None' },
          {
            id: ALERT_FILTER_OPTION.FIRING,
            text: 'Firing'
          },
          {
            id: ALERT_FILTER_OPTION.FIRED,
            text: 'Fired'
          },
          {
            id: ALERT_FILTER_OPTION.SNOOZED,
            text: 'Snoozed'
          },
          {
            id: ALERT_FILTER_OPTION.CLEAN,
            text: 'Clean'
          }
        ]
      },
      isExpandStatusIndicators: false
    };
  },
  computed: {
    ...mapGetters(['isLargeScreen']),
    ...mapGetters('alertsSuspiciousTrafficStore', [
      'alertListOfSelectedASN',
      'lastFiringAlertObj',
      'triggerListObj',
      'topTriggerList',
      'selectedTriggerDetails',
      'firingAlertsAmountOfSelectedASN'
    ]),
    ...mapState('alertsSuspiciousTrafficStore', [
      'isShowTriggerDetailsModal',
      'selectedTrigger'
    ])
  },
  watch: {
    isExpandStatusIndicators(val) {
      if (val && this.$refs.accordionContent) {
        setTimeout(() => {
          scrollToShowElementBottom(this.$refs.accordionContent);
        }, 500);
      }
    }
  },
  methods: {
    ...mapActions('alertsSuspiciousTrafficStore', [
      'fetchTrigger',
      'unSnoozeAlert',
      'fetchAverageHit'
    ]),
    ...mapMutations('alertsSuspiciousTrafficStore', [
      'setEditingAlertObj',
      'setIsEditingAlert',
      'setIsShowCreateAlertModal',
      'setIsShowSnoozeAlertModal',
      'setIsShowDeleteAlertModal',
      'setIsShowTriggerDetailsModal',
      'setSelectedTrigger'
    ]),

    showCreateAlertModal() {
      this.fetchAverageHit();
      this.setIsShowCreateAlertModal(true);
    },

    handleSortChange(val) {
      this.sortType = val;
    },

    handleSortToolChange(val) {
      this.sortType = val;
    },

    handleFilterToolChange(val) {
      this.filterType = val;
    },

    handleViewTriggerDetails(val) {
      this.fetchTrigger({ trigger: val, isTriggerOfSelectedAlert: false });
      this.setSelectedTrigger(val);
    },

    editAlert(val) {
      const editingAlert = this.alertListOfSelectedASN.find(
        alert => alert.id === val
      );

      this.setEditingAlertObj(editingAlert);
      this.setIsEditingAlert(true);
      this.fetchAverageHit();
      this.setIsShowCreateAlertModal(true);
    },

    snoozeAlert(val) {
      const snoozingAlert = this.alertListOfSelectedASN.find(
        alert => alert.id === val
      );

      this.setEditingAlertObj(snoozingAlert);
      this.setIsShowSnoozeAlertModal(true);
    },

    unSnooze(val) {
      const unSnoozingAlert = this.alertListOfSelectedASN.find(
        alert => alert.id === val
      );
      this.setEditingAlertObj(unSnoozingAlert);
      this.unSnoozeAlert();
    },

    deleteAlert(val) {
      const deletingAlert = this.alertListOfSelectedASN.find(
        alert => alert.id === val
      );

      this.setEditingAlertObj(deletingAlert);
      this.setIsShowDeleteAlertModal(true);
    },

    showSearchBox() {
      this.isShowSearchAlert = true;
    },

    closeSearchBox() {
      this.isShowSearchAlert = false;
    },

    toggleIsShowSortTool() {
      this.isShowSortTool = !this.isShowSortTool;
      if (!this.isShowSortTool) this.sortType = ALERT_SORT_OPTION.NONE;
    },

    toggleIsShowFilterTool() {
      this.isShowFilterTool = !this.isShowFilterTool;
      if (!this.isShowFilterTool) this.filterType = ALERT_FILTER_OPTION.NONE;
    },

    handleCloseFilterTool() {
      if (this.isShowFilterTool) this.toggleIsShowFilterTool();
    }
  }
};
</script>

<style lang="scss" scoped>
.overview {
  margin-bottom: 3rem;

  .overview-title {
    margin-bottom: 1.5rem;
  }
}
h2 {
  font-size: 1.75rem;
}

.alerts-title {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    margin: 0;
  }

  .search-and-create {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;

    .create-alert {
      margin-left: 2rem;
    }

    .icon-container {
      width: 2.5rem;

      svg {
        margin-left: 1rem;
        font-size: 1.25rem;
        color: $icon-grey;
        cursor: pointer;
      }
    }
  }
}

.status-indicators-content {
  border: none;
  padding: 0;
  opacity: 0;
  height: auto;
  max-height: 0;
  overflow: hidden;
  transition-property: opacity max-height;
  transition-duration: 0.5s;

  &.show {
    opacity: 1;
    padding-top: 1rem;
    margin-bottom: 1rem;
    max-height: 50rem;
    border-top: 1px solid #bbb;
    border-bottom: 1px solid #bbb;
  }
}

.overview-contents {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;

  & > div {
    flex: 0 1 auto;
    width: 49%;
  }
}

.mobile {
  .indicator-toggle {
    display: flex;
    align-items: center;
    margin: 0;
    width: 16rem;

    p {
      margin: 0;
      border-bottom: 1px solid $text-black;
      cursor: pointer;
    }

    .icon-wrapper {
      padding: 0.5rem;
      padding-right: 1.5rem;
      cursor: pointer;

      svg {
        transition: transform 0.5s;
      }
    }
  }

  .status-indicators-content.show {
    border: none;
  }

  .overview-contents {
    flex-direction: column;

    & > div {
      flex: auto;
      width: 100%;
    }

    & > div:nth-child(1) {
      margin-right: 0;
      margin-bottom: 1rem;
    }
  }

  .alerts-title {
    flex-wrap: wrap;
  }

  .search-alert {
    margin: 0.5rem 0;
  }

  .icon-group-and-search {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;

    .icon {
      padding: 0.5rem;
      margin-right: 0.5rem;
      width: 2rem;
      height: 2rem;
      color: $icon-grey;
    }

    .active {
      color: $text-light-blue;
    }
  }

  .tool {
    padding-bottom: 0.5rem;
  }
}
</style>
